import React, { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DescriptionIcon from '@mui/icons-material/Description'
import BusinessIcon from '@mui/icons-material/Business'
import PeopleIcon from '@mui/icons-material/People'
import SettingsIcon from '@mui/icons-material/Settings'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import DnsIcon from '@mui/icons-material/Dns'
import ArticleIcon from '@mui/icons-material/Article'
import EngineeringIcon from '@mui/icons-material/Engineering'
import HistoryIcon from '@mui/icons-material/History'
import BuildIcon from '@mui/icons-material/Build'
import { useAuth } from '../../Provider/AuthProvider'
import { useMenuItems } from '../../Services/MenuConfig'
import { doLogout } from '../../State/Functions'
import { FC } from '../../Services/FeathersClient'

const ICON_MAP = {
  Dashboard: <DashboardIcon />,
  Description: <DescriptionIcon />,
  Business: <BusinessIcon />,
  People: <PeopleIcon />,
  Settings: <SettingsIcon />,
  DesktopWindows: <DesktopWindowsIcon />,
  Dns: <DnsIcon />,
  Article: <ArticleIcon />,
  Engineering: <EngineeringIcon />,
  History: <HistoryIcon />,
  Build: <BuildIcon />
}

const DRAWER_WIDTH = 240
const DRAWER_COLLAPSED = 64

const ROLE_LABELS = {
  siapAdmin: 'Super Admin',
  siapDeveloper: 'Developer SIAP',
  agencyAdmin: 'Admin Agenzia'
}

export const AppLayout = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const location = useLocation()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const userData = authUser?.user
  const menuItems = useMenuItems()
  const [buildInfo, setBuildInfo] = useState(null)

  useEffect(() => {
    FC.service('versions').get('build-info')
      .then(setBuildInfo)
      .catch(() => {})
  }, [])

  const drawerWidth = collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen)
  const handleCollapseToggle = () => setCollapsed(!collapsed)
  const handleUserMenuOpen = (e) => setAnchorEl(e.currentTarget)
  const handleUserMenuClose = () => setAnchorEl(null)

  const handleLogout = () => {
    handleUserMenuClose()
    doLogout()
  }

  const handleNavigate = (path) => {
    navigate(`/${path}`)
    if (isMobile) setMobileOpen(false)
  }

  const isActive = (path) => {
    const fullPath = `/${path}`
    if (fullPath === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/'
    return location.pathname.startsWith(fullPath)
  }

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          px: collapsed && !isMobile ? 1 : 2
        }}
      >
        {(!collapsed || isMobile) && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0, userSelect: 'none' }}>
            <Typography variant='h6' sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: -0.5 }}>
              CRSLINK
            </Typography>
            <Typography variant='h6' sx={{ fontWeight: 800, color: 'primary.main' }}>
              2
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton onClick={handleCollapseToggle} size='small'>
            {collapsed
              ? <MenuIcon fontSize='small' />
              : <ChevronLeftIcon fontSize='small' />}
          </IconButton>
        )}
      </Toolbar>

      <Divider />

      <List sx={{ flex: 1, pt: 1, px: collapsed && !isMobile ? 0.5 : 1 }}>
        {(menuItems || []).filter((item) => item.path !== 'logout').map((item) => {
          const active = isActive(item.path)
          const button = (
            <ListItemButton
              key={item.path}
              selected={active}
              onClick={() => handleNavigate(item.path)}
              sx={{
                minHeight: 48,
                borderRadius: 2,
                mb: 0.5,
                justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                px: collapsed && !isMobile ? 1.5 : 2
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed && !isMobile ? 0 : 40,
                  justifyContent: 'center',
                  color: active ? 'primary.main' : 'text.secondary'
                }}
              >
                {ICON_MAP[item.icon] || <DashboardIcon />}
              </ListItemIcon>
              {(!collapsed || isMobile) && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 600 : 500
                  }}
                />
              )}
            </ListItemButton>
          )

          if (collapsed && !isMobile) {
            return (
              <Tooltip key={item.path} title={item.label} placement='right' arrow>
                {button}
              </Tooltip>
            )
          }
          return button
        })}
      </List>

      <Divider />

      <Box sx={{ p: collapsed && !isMobile ? 1 : 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {(!collapsed || isMobile) && userData && (
          <>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
              {userData.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                {userData.username}
              </Typography>
              <Typography variant='caption' color='text.secondary' noWrap>
                {ROLE_LABELS[userData.role] || userData.role}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {(!collapsed || isMobile) && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant='caption' color='text.disabled' sx={{ fontFamily: 'monospace', fontSize: 10, lineHeight: 1.4, display: 'block' }}>
            Web v{__WEB_VERSION__}{buildInfo ? ` \u00b7 API v${buildInfo.api?.version}` : ''}
          </Typography>
          {buildInfo?.client?.version && (
            <Typography variant='caption' color='text.disabled' sx={{ fontFamily: 'monospace', fontSize: 10, lineHeight: 1.4, display: 'block' }}>
              Client {buildInfo.client.version}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
          })
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton onClick={handleDrawerToggle} edge='start' sx={{ color: 'text.primary' }}>
                <MenuIcon />
              </IconButton>
            )}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', userSelect: 'none' }}>
                <Typography variant='h6' sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: -0.5 }}>
                  CRSLINK
                </Typography>
                <Typography variant='h6' sx={{ fontWeight: 800, color: 'primary.main' }}>
                  2
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {userData?.allAgencies?.length > 0 && (
              <Chip
                label={userData.allAgencies.map((a) => a.name).join(', ')}
                size='small'
                variant='outlined'
                sx={{ fontWeight: 500, display: { xs: 'none', sm: 'flex' }, maxWidth: 300 }}
                title={userData.allAgencies.map((a) => a.name).join(', ')}
              />
            )}
            <Tooltip title='Account'>
              <IconButton onClick={handleUserMenuOpen} size='small'>
                <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 }}>
                  {userData?.username?.charAt(0).toUpperCase() || <PersonIcon />}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              slotProps={{
                paper: {
                  sx: { mt: 1, minWidth: 180, borderRadius: 2 }
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {userData?.username}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {ROLE_LABELS[userData?.role] || userData?.role}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ gap: 1.5, color: 'error.main', mt: 0.5 }}>
                <LogoutIcon fontSize='small' />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar - Mobile */}
      {isMobile && (
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH
            }
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Drawer
          variant='permanent'
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              }),
              overflowX: 'hidden'
            }
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
          })
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
